/**
 * PokemonSilhouette — Nexus Dex per-species silhouette renderer.
 *
 * Renders a single tile from the bundled Style B silhouette atlas
 * (client/src/assets/silhouettes/dex-silhouettes-{dark,light}.png +
 * .atlas.json). The tiles are pre-coloured at build time (see
 * scripts/silhouettes/build.ts): a baked neon-green outline, a near-black
 * (dark) / near-white (light) interior at 88%, and a soft outer glow. We
 * never ship colour artwork — only the traced shape.
 *
 * Theming is by ATLAS SELECTION, not CSS filters: dark theme uses the dark
 * page (lime outline, near-black body); light theme uses the light page
 * (forest-green outline, near-white body). The component watches the `.dark`
 * class on <html> (owned by SessionProvider) and swaps the page accordingly.
 *
 * Forms: pass `formSlug` (e.g. "3-mega", "6-mega-x", "25-gmax",
 * "892-1-gmax") to render a Mega / G-Max tile from the atlas `forms` map;
 * it overrides `id` for tile lookup.
 */

import { memo, useSyncExternalStore } from "react";
import type React from "react";
import atlas from "@/assets/silhouettes/dex-silhouettes.atlas.json";

// The atlas ships as several 4096x4096 PAGES per theme, not one giant sheet: a
// single 8192x8960 sheet decodes to ~294 MB of RGBA, which Android's WebView
// refuses to allocate, so every silhouette rendered blank on Android (iOS was
// fine). Each page is at/under the 4096 GL_MAX_TEXTURE_SIZE guaranteed on every
// device and ~67 MB decoded. import.meta.glob pulls in every page URL so the set
// tracks the manifest's `pageCount` with no per-page import to maintain.
function pagesFromGlob(mods: Record<string, unknown>): string[] {
  return Object.entries(mods)
    .map(([p, url]) => ({
      idx: Number(p.match(/-(\d+)\.png$/)?.[1] ?? 0),
      url: url as string,
    }))
    .sort((a, b) => a.idx - b.idx)
    .map((e) => e.url);
}
const DARK_PAGES = pagesFromGlob(
  import.meta.glob("@/assets/silhouettes/dex-silhouettes-dark-*.png", {
    eager: true,
    import: "default",
  }),
);
const LIGHT_PAGES = pagesFromGlob(
  import.meta.glob("@/assets/silhouettes/dex-silhouettes-light-*.png", {
    eager: true,
    import: "default",
  }),
);

interface PokemonSilhouetteProps {
  id: number;
  size?: number;
  caught?: boolean;
  shiny?: boolean;
  /** Tint silhouette as "missing" (default uncaught treatment) vs simple grey. */
  variant?: "default" | "locked";
  /** Render a Mega/G-Max form tile (overrides `id`). e.g. "6-mega-x". */
  formSlug?: string;
  ariaLabel?: string;
  className?: string;
  /**
   * E43: when true the silhouette fills its parent box (width/height 100%,
   * the atlas tile scaled to the box's measured size) instead of a fixed
   * `size` px square. The dex cell uses this so the sprite is sized by the
   * cell's sprite region, never overflowing the rounded border.
   */
  fill?: boolean;
}

type TileCoord = { page: number; row: number; col: number };

const TILES = atlas.tiles as Record<string, TileCoord>;
const FORMS = (atlas.forms ?? {}) as Record<string, TileCoord>;

/**
 * Track the active theme by observing the `.dark` class on <html>.
 * SessionProvider toggles that class; we mirror it so the right atlas page
 * is selected without prop-drilling theme through every call site.
 *
 * ONE observer for the whole app (2026-09-22 Galaxy S21+ Dex lag): the dex
 * grid mounts ~1,000 silhouettes at once and each used to register its own
 * MutationObserver on <html>, a thousand observers in one commit and a
 * thousand callbacks on every theme flip. A module-level store with a single
 * observer fans the class change out to every subscribed cell instead.
 */
const themeListeners = new Set<() => void>();
let themeObserver: MutationObserver | null = null;
function readIsDark(): boolean {
  // Default to dark (the app's brand default + unauthenticated surfaces).
  if (typeof document === "undefined") return true;
  return document.documentElement.classList.contains("dark");
}
/**
 * True under a faction accent (SessionProvider stamps html[data-accent]).
 * The brand accent sets no attribute, so the baked green tile is drawn as is.
 */
function readAccentTinted(): boolean {
  if (typeof document === "undefined") return false;
  const accent = document.documentElement.getAttribute("data-accent");
  return accent != null && accent !== "" && accent !== "nexus";
}
function subscribeTheme(listener: () => void): () => void {
  themeListeners.add(listener);
  if (!themeObserver && typeof document !== "undefined") {
    themeObserver = new MutationObserver(() => {
      for (const l of themeListeners) l();
    });
    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class", "data-accent"],
    });
  }
  return () => {
    themeListeners.delete(listener);
    if (themeListeners.size === 0 && themeObserver) {
      themeObserver.disconnect();
      themeObserver = null;
    }
  };
}
function useIsDarkTheme(): boolean {
  return useSyncExternalStore(subscribeTheme, readIsDark, () => true);
}
function useAccentTinted(): boolean {
  return useSyncExternalStore(subscribeTheme, readAccentTinted, () => false);
}

/**
 * Under a faction accent the tile becomes a MASK over var(--accent-fill)
 * (2026-09-22 owner ask: Instinct / Mystic / Valor silhouettes did not match
 * the theme). A hue-rotated green can only approximate an accent; a mask
 * filled with the accent token IS the accent, glow included, and costs no
 * filter pass. The brand accent keeps the baked green tile.
 */
function tileStyle(
  tinted: boolean,
  url: string,
  size: string,
  position: string,
): React.CSSProperties {
  if (!tinted) {
    return {
      backgroundImage: `url(${url})`,
      backgroundSize: size,
      backgroundPosition: position,
      backgroundRepeat: "no-repeat",
    };
  }
  return {
    backgroundColor: "var(--accent-fill)",
    WebkitMaskImage: `url(${url})`,
    maskImage: `url(${url})`,
    WebkitMaskSize: size,
    maskSize: size,
    WebkitMaskPosition: position,
    maskPosition: position,
    WebkitMaskRepeat: "no-repeat",
    maskRepeat: "no-repeat",
  };
}

/** Test seam: how many theme observers are live (0 or 1, never per cell). */
export function __silhouetteThemeObserverCount(): number {
  return themeObserver ? 1 : 0;
}

/**
 * Resolve the atlas tile coordinate for a form slug or a national-dex id, or
 * null when the id names no species at all.
 *
 * The null matters. Callers pass a deliberate 0 for "species unknown" (a raid
 * boss whose dex id upstream never gave us, see RaidBossPanel), and this used
 * to clamp that straight to 1 — so an unresolved boss drew BULBASAUR's
 * silhouette under its own name. Reported 2026-09-03 against "Mega Starmie",
 * which LeekDuck had published under a name-based icon URL with no dex number
 * in it. Drawing a confidently wrong species is worse than drawing nothing:
 * the clamp only ever produced a lie.
 */
function resolveCoord(id: number, formSlug?: string): TileCoord | null {
  if (formSlug && FORMS[formSlug]) return FORMS[formSlug];
  if (!Number.isFinite(id) || id < 1) return null;
  const clamped = Math.min(atlas.total, id);
  return TILES[String(clamped)] ?? null;
}

function PokemonSilhouetteInner({
  id,
  size = 36,
  caught = false,
  shiny = false,
  variant = "default",
  formSlug,
  ariaLabel,
  className = "",
  fill = false,
}: PokemonSilhouetteProps) {
  const isDark = useIsDarkTheme();
  const tinted = useAccentTinted();
  const tile = atlas.tileSize;
  // Per-PAGE geometry (every page is pageColumns x pageRows tiles at
  // pageWidth x pageHeight px). Fill mode addresses one tile with PERCENTAGE
  // background-position, which needs the column/row counts; fixed mode uses the
  // page pixel size below.
  const cols = atlas.pageColumns;
  const rows = atlas.pageRows;
  const sheetW = atlas.pageWidth;
  const sheetH = atlas.pageHeight;

  const coord = resolveCoord(id, formSlug);
  const pages = isDark ? DARK_PAGES : LIGHT_PAGES;
  const sheetUrl = coord ? (pages[coord.page] ?? pages[0]) : "";
  const { page, row, col } = coord ?? { page: 0, row: 0, col: 0 };

  // E58 fill mode (replaces E43 measured-transform): size the tile to the box
  // with PURE CSS — no ResizeObserver, no JS-measured transform scale. The old
  // approach seeded `measured` at the fixed `size` (36) and only updated when a
  // ResizeObserver read a non-zero box; in the VIRTUALIZED dex grid, cells mount
  // and recycle so fast that the box frequently measured 0 (or the [fill]-only
  // effect never re-ran for a recycled node), leaving scale stuck at 36/256 ≈
  // 0.14 — the silhouette shrank to a tiny squiggle and looked "disappeared".
  // The CAUGHT sprite never had this bug because it uses an <img> with plain
  // `width/height:100% + object-fit:contain` (declarative, no measurement).
  //
  // We now match that: render the sheet as a background on a 100%×100% box and
  // let `background-size` + percentage `background-position` pick exactly one
  // tile, scaled to the box by the browser. This is responsive on every phone
  // size with zero runtime measurement.
  //   background-size: (cols*100%) (rows*100%)  -> one tile == one box
  //   background-position: percentage that selects the (col,row) tile
  // For an N-tile axis the position percentage is index/(N-1)*100%.
  // Unknown species: an empty, correctly sized slot. It reads as "we have no
  // artwork for this" instead of naming the wrong Pokemon.
  if (!coord) {
    return (
      <span
        role="img"
        aria-label={ariaLabel ?? "Unknown species"}
        className={`pokemon-silhouette is-unknown ${className}`}
        data-species-id={id}
        data-unknown="true"
        style={{
          display: fill ? "block" : "inline-block",
          width: fill ? "100%" : size,
          height: fill ? "100%" : size,
          lineHeight: 0,
          verticalAlign: fill ? undefined : "middle",
        }}
      />
    );
  }

  if (fill) {
    const posX = cols > 1 ? (col / (cols - 1)) * 100 : 0;
    const posY = rows > 1 ? (row / (rows - 1)) * 100 : 0;
    return (
      <span
        role="img"
        aria-label={ariaLabel ?? `Species ${id}`}
        className={`pokemon-silhouette ${caught ? "is-caught" : "is-uncaught"} ${variant === "locked" ? "is-locked" : ""} ${className}`}
        data-species-id={id}
        data-form-slug={formSlug ?? ""}
        data-caught={caught ? "true" : "false"}
        data-shiny={shiny ? "true" : "false"}
        data-fill="true"
        data-tinted={tinted ? "true" : "false"}
        style={{
          display: "block",
          width: "100%",
          height: "100%",
          lineHeight: 0,
          ...tileStyle(tinted, sheetUrl, `${cols * 100}% ${rows * 100}%`, `${posX}% ${posY}%`),
        }}
      />
    );
  }

  // Fixed-size mode (non-grid call sites): keep the original `size`px square
  // rendered via a scaled tile. Unaffected by the virtualization sizing bug.
  const scale = size / tile;
  const ox = -col * tile;
  const oy = -row * tile;
  return (
    <span
      role="img"
      aria-label={ariaLabel ?? `Species ${id}`}
      className={`pokemon-silhouette ${caught ? "is-caught" : "is-uncaught"} ${variant === "locked" ? "is-locked" : ""} ${className}`}
      data-species-id={id}
      data-form-slug={formSlug ?? ""}
      data-caught={caught ? "true" : "false"}
      data-shiny={shiny ? "true" : "false"}
      style={{
        display: "inline-block",
        width: size,
        height: size,
        position: "relative",
        overflow: "hidden",
        lineHeight: 0,
        verticalAlign: "middle",
      }}
    >
      <span
        aria-hidden
        style={{
          display: "block",
          width: tile,
          height: tile,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          ...tileStyle(tinted, sheetUrl, `${sheetW}px ${sheetH}px`, `${ox}px ${oy}px`),
        }}
      />
    </span>
  );
}

// Memoised (2026-10-06): a dex switch re-renders every grid cell, and the
// sprite under a cell whose art did not change (every missing cell, and
// every caught cell between two dexes that share art) used to re-run its
// hooks and atlas lookups anyway. Props are plain values and a stable array.
export const PokemonSilhouette = memo(PokemonSilhouetteInner);
